import { useState, useEffect } from 'react';
import { useOnboardingState, useOnboardingDispatch } from '../../context/OnboardingContext';
import { useStepNavigation } from '../../hooks/useStepNavigation';
import { useOnboardingBlocker } from '../../hooks/useOnboardingBlocker';
import { useAuth } from '../../hooks/useAuth';
import { api } from '../../services/api';
import { useLanguage } from '../../i18n/LanguageContext';
import { LookupEntryResponse } from '../../types';
import MapPicker from '../../components/MapPicker';
import MissingFieldsHint from '../../components/onboarding/MissingFieldsHint';

export default function GarageInfoStep() {
  const state = useOnboardingState();
  const dispatch = useOnboardingDispatch();
  const { goNext, goBack } = useStepNavigation();
  const auth = useAuth();
  const { t, lang } = useLanguage();
  useOnboardingBlocker();

  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [cities, setCities] = useState<LookupEntryResponse[]>([]);
  const [areas, setAreas] = useState<LookupEntryResponse[]>([]);
  const [cityId, setCityId] = useState<string>('');
  const [areaId, setAreaId] = useState<string>('');
  const [areaFreeText, setAreaFreeText] = useState('');

  const info = state.garageInfo;

  const update = (data: Partial<typeof info>) => {
    dispatch({ type: 'SET_GARAGE_INFO', data });
  };

  // Fetch cities on mount
  useEffect(() => {
    api.fetchLookupEntries('CITIES').then(setCities).catch(() => {});
  }, []);

  // Fetch areas when city changes
  useEffect(() => {
    if (cityId) {
      api.fetchLookupEntries('AREAS', cityId).then(setAreas).catch(() => setAreas([]));
    } else {
      setAreas([]);
    }
  }, [cityId]);

  // Initialize cityId/areaId from backend data if available
  useEffect(() => {
    if (info.city && cities.length > 0 && !cityId) {
      const found = cities.find(c => c.name === info.city || c.id === (info as any).cityId);
      if (found) setCityId(found.id);
    }
  }, [info.city, cities]);

  const handleCityChange = (newCityId: string) => {
    setCityId(newCityId);
    setAreaId('');
    setAreaFreeText('');
    const city = cities.find(c => c.id === newCityId);
    update({ city: city?.name || '', area: '' });
  };

  const handleAreaChange = (newAreaId: string) => {
    setAreaId(newAreaId);
    const area = areas.find(a => a.id === newAreaId);
    update({ area: area?.name || '' });
  };

  const handleMapChange = (lat: number, lng: number) => {
    update({ latitude: lat, longitude: lng });
  };

  const hasPin = info.latitude != null && info.longitude != null;
  const hasArea = areaId || areaFreeText.trim();
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  const isEmailValid = emailRegex.test(info.email.trim());
  const canContinue = info.name.trim() !== '' && info.phone.trim() !== '' && isEmailValid && cityId !== '' && !!hasArea && hasPin;

  // #430 — name the mandatory fields still missing so the partner knows what to fix.
  const missingFields: string[] = [];
  if (info.name.trim() === '') missingFields.push(t('onboarding.garageInfo.name') || 'Garage Name');
  if (info.phone.trim() === '') missingFields.push(t('onboarding.garageInfo.phone') || 'Garage Phone Number');
  if (!isEmailValid) missingFields.push(t('onboarding.garageInfo.email') || 'Garage Email');
  if (cityId === '') missingFields.push(t('onboarding.garageInfo.city') || 'City');
  if (!hasArea) missingFields.push(t('onboarding.garageInfo.area') || 'Area/District');
  if (!hasPin) missingFields.push(t('onboarding.garageInfo.mapPicker') || 'Location pin on the map');

  const handleContinue = async () => {
    if (!canContinue || !auth.token) return;

    if (!hasPin) {
      setError(t('onboarding.garageInfo.mapRequired') || 'Please place a pin on the map to mark your garage location');
      return;
    }

    setSaving(true);
    setError(null);
    try {
      await api.updateDraft({
        name: info.name,
        phone: info.phone,
        email: info.email.trim(),
        city: info.city,
        area: areaFreeText || info.area,
        cityId: cityId || undefined,
        areaId: areaId || undefined,
        streetAddress: info.streetAddress || undefined,
        managerName: info.managerName || undefined,
        dailyCapacity: info.dailyCapacity ?? undefined,
        numberOfWorkers: info.numberOfWorkers ?? undefined,
        latitude: info.latitude ?? undefined,
        longitude: info.longitude ?? undefined,
        onboardingStep: 3,
      }, auth.token);
      goNext();
    } catch (err) {
      setError(err instanceof Error ? err.message : t('common.saveFailed'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div>
      <h2 className="wizard-step-title">{t('onboarding.garageInfo.title') || 'Enter Garage Basic Information'}</h2>

      <div className="wizard-form-row">
        <div className="wizard-form-group">
          <label>{t('onboarding.garageInfo.name') || 'Garage Name'} *</label>
          <input type="text" placeholder={t('onboarding.garageInfo.name.placeholder') || 'Garage Name'} value={info.name} onChange={(e) => update({ name: e.target.value })} />
        </div>
        <div className="wizard-form-group">
          <label>{t('onboarding.garageInfo.phone') || 'Garage Phone Number'} *</label>
          <input type="tel" placeholder={t('onboarding.garageInfo.phone.placeholder') || 'Enter garage phone number'} value={info.phone} onChange={(e) => update({ phone: e.target.value })} />
        </div>
      </div>

      <div className="wizard-form-row">
        <div className="wizard-form-group">
          <label>{t('onboarding.garageInfo.email') || 'Garage Email'} *</label>
          <input
            type="email"
            placeholder={t('onboarding.garageInfo.email.placeholder') || 'Enter garage email'}
            value={info.email}
            onChange={(e) => update({ email: e.target.value })}
          />
          {info.email.trim() !== '' && !isEmailValid && (
            <span className="field-error" style={{ color: '#dc2626', fontSize: '0.8125rem', marginTop: '0.25rem' }}>
              {t('onboarding.garageInfo.email.invalid') || 'Please enter a valid email address'}
            </span>
          )}
        </div>
        <div className="wizard-form-group">
          <label>{t('onboarding.garageInfo.manager') || 'Manager Name'}</label>
          <input type="text" placeholder={t('onboarding.garageInfo.manager.placeholder') || 'Manager Name'} value={info.managerName} onChange={(e) => update({ managerName: e.target.value })} />
        </div>
      </div>

      <div className="wizard-form-group">
        <label>{t('onboarding.garageInfo.carCapacity') || 'Car Capacity'}</label>
        <input
          type="number"
          min="0"
          placeholder={t('onboarding.garageInfo.carCapacity.placeholder') || 'Enter car capacity'}
          value={info.dailyCapacity ?? ''}
          onChange={(e) => update({ dailyCapacity: e.target.value ? parseInt(e.target.value, 10) : null })}
        />
      </div>

      <h3 className="wizard-step-title" style={{ marginTop: '2rem' }}>{t('onboarding.garageInfo.location') || 'Garage Location'}</h3>

      <div className="garage-location-layout">
        <div className="garage-location-fields">
          <div className="wizard-form-group">
            <label>{t('onboarding.garageInfo.city') || 'Select City'} *</label>
            <select value={cityId} onChange={(e) => handleCityChange(e.target.value)}>
              <option value="">{t('onboarding.garageInfo.city.placeholder') || 'Select City'}</option>
              {cities.map(city => (
                <option key={city.id} value={city.id}>
                  {lang === 'ar' && city.nameAr ? city.nameAr : city.name}
                </option>
              ))}
            </select>
          </div>

          <div className="wizard-form-group">
            <label>{t('onboarding.garageInfo.area') || 'Select Area/District'} *</label>
            {areas.length > 0 ? (
              <select value={areaId} onChange={(e) => handleAreaChange(e.target.value)}>
                <option value="">{t('onboarding.garageInfo.area.placeholder') || 'Select Area/District'}</option>
                {areas.map(area => (
                  <option key={area.id} value={area.id}>
                    {lang === 'ar' && area.nameAr ? area.nameAr : area.name}
                  </option>
                ))}
              </select>
            ) : (
              <input
                type="text"
                placeholder={t('onboarding.garageInfo.area.placeholder') || 'Enter Area/District'}
                value={areaFreeText}
                onChange={(e) => {
                  setAreaFreeText(e.target.value);
                  update({ area: e.target.value });
                }}
              />
            )}
          </div>

          <div className="wizard-form-group">
            <label>{t('onboarding.garageInfo.street') || 'Street Address'}</label>
            <input type="text" placeholder={t('onboarding.garageInfo.street.placeholder') || 'Street Address'} value={info.streetAddress} onChange={(e) => update({ streetAddress: e.target.value })} />
          </div>
        </div>

        <div className="garage-location-map">
          <label>{t('onboarding.garageInfo.mapPicker') || 'Map Picker'}</label>
          <MapPicker
            latitude={info.latitude}
            longitude={info.longitude}
            onChange={handleMapChange}
            searchPlaceholder={t('onboarding.garageInfo.mapSearch') || 'Search for a place, mall, or street'}
          />
          {!hasPin && (
            <p className="map-hint">{t('onboarding.garageInfo.mapHint') || 'Click on the map to place your garage location'}</p>
          )}
        </div>
      </div>

      {error && <div className="error-message">{error}</div>}

      {!canContinue && <MissingFieldsHint missing={missingFields} />}

      <div className="wizard-btn-row">
        <button className="wizard-btn wizard-btn-secondary" onClick={goBack}>{t('common.back') || 'Back'}</button>
        <button className="wizard-btn wizard-btn-primary" disabled={!canContinue || saving} onClick={handleContinue}>
          {saving ? (t('common.saving') || 'Saving...') : (t('common.continue') || 'Next')}
        </button>
      </div>
    </div>
  );
}
